import { FormGroup, FormHelperText, FormLabel, MenuItem, Select, TextField } from "@mui/material";
import { FC, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Collaborator } from "src/apis/company";
import { companyRequestReadableTypes, createCompanyRequest, CreateCompnayRequestPayload } from "src/apis/company_requests";
import CollaboratorSelect from "src/pages/components/CollaboratorSelect";
import FormSaveAndResetButtons from "src/pages/components/FormSaveAndResetButtons";
import PageContainer from "src/pages/components/PageContainer";
import { getUserCompanyId, getUserId, isUserAdministrator, isUserCompany } from "src/utils/auth";
import { isSuccessOrAllRequiredFieldsAreFilled } from "src/utils/form";
import { Id } from "src/utils/types";

const initialValues: CreateCompnayRequestPayload = {
  collaborators_ids: [],
  message: '',
  request_type: '',
  requested_by_company_id: '',
  requested_by_user_id: '',
}

const RegisterCompanyRequest: FC = (): JSX.Element => {
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [formValues, setFormValues] = useState<CreateCompnayRequestPayload>(initialValues);
  const [collaboratorId, setCollaboratorId] = useState<Id>('');
  const {id} = useParams();

  const isUpdate = useMemo<boolean>(() => Boolean(id), [id])
  const isSubmitDisabled = useMemo<boolean>(() => isSuccessOrAllRequiredFieldsAreFilled(success, formValues, ['collaborators_ids']), [success, formValues])
  const resetFormState = () => {
    setError('');
    setSuccess(false);
    setCollaboratorId('');
    setFormValues(initialValues);
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setLoading(true);

    try {
      await createCompanyRequest(formValues)

      setSuccess(true);
      setError('')
    } catch (err: any) {
      setSuccess(false);
      setError(err.error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    setFormValues(previousValues => ({
      ...previousValues,
      requested_by_user_id: getUserId(),
      requested_by_company_id: getUserCompanyId(),
    }))
  }, [])

  return (
    <PageContainer
      title={isUpdate ? "Editando solicitação" : "Adicionar solicitação"}
      error={error}
      guard={() => isUserCompany() && !isUserAdministrator()}
    >
      <form
        onSubmit={handleSubmit}
        style={{ display: "flex", flexDirection: "column", gap: "1rem" }}
      >
        <FormGroup>
          <FormLabel>Tipo de solicitação</FormLabel>
          <Select
            labelId="request_type"
            name="request_type"
            variant="outlined"
            value={formValues?.request_type ?? ''}
            onChange={(e) => {
              setFormValues((previousValues) => ({
                ...previousValues,
                request_type: e.target.value as string,
              }));
            }}
          >
            {Object.entries(companyRequestReadableTypes).map(([key, value]) => (
              <MenuItem key={key} value={String(key)}>
                {value}
              </MenuItem>
            ))}
          </Select>
        </FormGroup>

        <FormGroup>
          <CollaboratorSelect
            disabled={isUpdate}
            helperText="Colaborador envolvido nessa solicitação (Opcional)"
            collaboratorId={collaboratorId}
            companyId={formValues.requested_by_company_id}
            setCollaborator={(collaborator: Collaborator) => {
              setCollaboratorId(collaborator.id_colaborador);
              setFormValues((previousValues) => ({
                ...previousValues,
                collaborators_ids: [collaborator.id_colaborador]
              }));
            }}
            unsetCollaborator={(collaborator: Collaborator) => {
              setFormValues((previousValues) => ({
                ...previousValues,
                collaborators_ids: formValues?.collaborators_ids?.filter(
                  (collaboratorId) => collaboratorId !== collaborator?.id_colaborador
                ),
              }));
            }}
            setError={setError}
          />
        </FormGroup>

        <FormGroup>
          <FormLabel>Motivo</FormLabel>
          <TextField
            multiline
            minRows={4}
            type="text"
            value={formValues.message}
            onChange={(e) => {
              setFormValues(previousValues => ({
                ...previousValues,
                message: e.target.value as string,
              }));
            }}
          />
          <FormHelperText>
            Motivo ou descrição da solicitação ({formValues.message.length} de 1000 caracteres)
          </FormHelperText>
        </FormGroup>

        <FormSaveAndResetButtons
          success={success}
          loading={loading}
          isSubmitDisabled={isSubmitDisabled}
          isResetFormStateDisabled={isUpdate}
          resetFormState={resetFormState}
        />
      </form>
    </PageContainer>
  )
}

export default RegisterCompanyRequest;
